import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { Question } from '~src/interfaces/question.interface';
import { ContentLocalesI } from '~src/interfaces/session.types';
import {
  QuestionSubmit,
  deleteTopicQuestion,
  editTopicQuestion,
} from '~src/modules/topics/actions/topicActions';
import { StoreI } from '~src/rootReducer';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import logger from '~src/services/logger/LlsLogger';
import { translateContent } from '~src/services/translationServices';
import {
  validatorMaxLengthLink1000,
  validatorMaxLengthLink500,
  validatorRequired,
} from '~src/services/validators';
import SvgIcon from '~src/shared/SvgIcon';
import UrlText from '~src/shared/UrlText';
import CaretBtn from '~src/shared/buttons/caretBtn/CaretBtn';
import ActionConfirmDialog from '~src/shared/dialogs/actionConfirmDialog/ActionConfirmDialog';
import FrameworkDelAction from '~src/shared/form/FrameworkDelAction';
import TranslatableSectionForm from '~src/shared/form/translatableSectionForm/TranslatableSectionForm';
import { SectionOpt, TranslatableField } from '~src/shared/form/translatableSectionForm/interfaces';
import { DraggableIcon, DraggableInfo } from '~src/shared/lists/dragList.shared.sc';
import { composeValidators } from '~src/shared/reactFinalForm/validators';

import { TopicSubject } from '~src/interfaces/topic.interface';
import { Caret, DelContainer, FormContainer, OptionItemContainer } from './topicQuestionListItem.sc';

interface OwnProps {
  topicId: string;
  item: Question;
}

interface StateProps {
  loadingEditQuestion: boolean;
  loadingDelQuestion: boolean;
  editQuestionSections: SectionOpt[];
}

interface DispatchProps {
  editTopicQuestion(questionId: string, payload: QuestionSubmit): ApiReduxResp<Question>;
  deleteTopicQuestion(topicId: string, questionId: string, name: ContentLocalesI): ApiReduxResp<void>;
}

type Props = OwnProps & StateProps & DispatchProps & WithTranslation;

interface State {
  showEditForm: boolean;
  confirmDeletion: boolean;
  deletionError: string | null;
}

class TopicQuestionListItem extends React.Component<Props, State> {
  state = {
    showEditForm: false,
    confirmDeletion: false,
    deletionError: null,
  };

  onEditSubmit = (payload: Record<string, any>) => {
    const tmpPayload = { ...payload, isCommentEnabled: !payload.isCommentEnabled };
    if (!tmpPayload.selfTitle || !translateContent(tmpPayload.selfTitle)) {
      tmpPayload.selfTitle = tmpPayload.title;
    }
    return this.props.editTopicQuestion(this.props.item.id, tmpPayload).then((response) => {
      if (!response.error) {
        this.toggleShowEditForm();
      } else {
        logger.info('Error edit question of topic', {
          error: new Error('Error edit question of topic'),
          info: {
            componentStack: 'TopicQuestionListItem',
          },
        });
      }
      return response;
    });
  };

  toggleShowEditForm = () => {
    this.setState((prevState: State) => ({ showEditForm: !prevState.showEditForm }));
  };

  toggleConfirmDeletionModal = () => {
    this.setState((prevState: State) => ({
      confirmDeletion: !prevState.confirmDeletion,
      deletionError: null,
    }));
  };

  handleDelete = () => {
    this.props
      .deleteTopicQuestion(this.props.topicId, this.props.item.id, this.props.item.title)
      .then((response) => {
        if (response.error) {
          this.setState({ deletionError: 'x.surveys.schedules.delete.error.question_error' });
        }
      });
  };

  render() {
    const { t, item } = this.props;

    return (
      <>
        <ActionConfirmDialog
          t={t}
          show={this.state.confirmDeletion}
          close
          onHide={this.toggleConfirmDeletionModal}
          actionTitleKey="x.question.delete_confirmation.headline"
          actionContentKey="x.question.delete_confirmation.content"
          actionCall={this.handleDelete}
          showDeletion
          loadingAction={this.props.loadingEditQuestion}
          actionError={this.state.deletionError}
        />
        <OptionItemContainer>
          <DraggableInfo>
            <DraggableIcon>
              <SvgIcon name="iconDraggable" width="18px" height="14px" />
            </DraggableIcon>
            <span>
              <UrlText blocked text={translateContent(item.title)} />
            </span>
          </DraggableInfo>
          <Caret onClick={this.toggleShowEditForm}>
            <CaretBtn open={this.state.showEditForm} size="18px" />
          </Caret>
        </OptionItemContainer>
        {this.state.showEditForm && (
          <FormContainer>
            <TranslatableSectionForm
              t={t}
              form={`editQuestion_${item.id}`}
              sections={this.props.editQuestionSections}
              loadingSave={this.props.loadingEditQuestion}
              onSubmit={this.onEditSubmit}
              onCancel={this.toggleShowEditForm}
            />
            <DelContainer>
              <FrameworkDelAction
                t={t}
                delAction={this.toggleConfirmDeletionModal}
                isLoading={this.props.loadingDelQuestion}
                isFramework={item.framework}
              />
            </DelContainer>
          </FormContainer>
        )}
      </>
    );
  }
}

const mapStateToProps = ({ topics }: StoreI, { item }: OwnProps) => {
  let nameLabel = 'x.topics.details.question.add.name.placeholder';
  let editQuestionFields: TranslatableField[] = [];
  if (item.subject === TopicSubject.People) {
    nameLabel = 'x.topics.details.question.add.assesment_by_others.placeholder';
    editQuestionFields = [
      {
        name: 'selfTitle',
        label: 'x.topics.details.question.add.self_assessment.placeholder',
        value: item.selfTitle,
        validator: composeValidators([validatorRequired, validatorMaxLengthLink500]),
        translationValidator: validatorMaxLengthLink500,
        type: 'textArea',
      },
    ];
  }
  editQuestionFields = [
    {
      name: 'title',
      label: nameLabel,
      value: item.title,
      validator: composeValidators([validatorRequired, validatorMaxLengthLink500]),
      translationValidator: validatorMaxLengthLink500,
      type: 'textArea',
    },
    ...editQuestionFields,
    {
      name: 'description',
      label: 'x.topics.details.question.add.description.placeholder',
      value: item.description,
      validator: validatorMaxLengthLink1000,
      translationValidator: validatorMaxLengthLink1000,
      type: 'textArea',
    },
    {
      name: 'isCommentEnabled',
      label: 'x.topics.details.question.add.is_comment_enabled.placeholder',
      value: !item.isCommentEnabled,
      type: 'checkbox' as const,
    },
  ];

  const improveFields: TranslatableField[] = [
    {
      name: 'howToImprove',
      value: item.howToImprove,
      label: 'x.topics.details.question.improve.manager',
      type: 'textArea',
    },
    {
      name: 'howToImproveSelf',
      value: item.howToImproveSelf,
      label: 'x.topics.details.question.improve.own',
      type: 'textArea',
    },
  ];

  const editQuestionSections: SectionOpt[] = [
    {
      name: 'info',
      fields: editQuestionFields,
    },
    {
      name: 'improve',
      titleKey: 'x.topics.details.section.improve.label',
      descriptionKey: 'x.topics.details.section.improve.descr',
      fields: improveFields,
    },
  ];

  return {
    loadingEditQuestion: topics.loadingEditQuestion,
    loadingDelQuestion: topics.loadingDelQuestion,
    editQuestionSections,
  };
};

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  editTopicQuestion: (questionId, payload) => dispatch(editTopicQuestion(questionId, payload)),
  deleteTopicQuestion: (topicId, questionId, name) =>
    dispatch(deleteTopicQuestion(topicId, questionId, name)),
});

const TopicQuestionListItemWithTranslate = withTranslation()(TopicQuestionListItem);
const TopicQuestionListItemSuperComponent = connect(
  mapStateToProps,
  mapDispatchToProps,
)(TopicQuestionListItemWithTranslate);

export default TopicQuestionListItemSuperComponent;
